// ...existing code...
import React, { useState } from 'react';
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { useInvoice } from "../../pages/InvoiceApp";
import { FileDown, Loader2, Save, Mail, Eye, Calculator } from "lucide-react";
import { InvoiceSummary } from "./InvoiceSummary";
import InvoicePreview from "./InvoicePreview";
import { cn } from "../../lib/utils";
import { useTheme } from '../ui/theme-provider';
import { generateInvoicePDFBlob, generateInvoicePDF } from "../../services/InvoicePDFGenerator";
import { practiceService } from "../../services/practiceService";
import { practiceDataUtil } from "../../helpers/practiceDataUtil";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';

// Modern, card-based, mobile-friendly Invoice Actions UX

export const InvoiceActions = React.memo(() => {
  // Memoized resolved practice for filename display (matches PDF generation logic)
  const [resolvedPractice, setResolvedPractice] = React.useState(null);

  const [status, setStatus] = useState({ type: null, message: "" });
  const [modal, setModal] = useState({ open: false, type: null });
  const [loading, setLoading] = useState({ save: false, pdf: false, email: false });
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const { theme } = useTheme();
  const [showSteps, setShowSteps] = useState(false);
  const [showCalculationsModal, setShowCalculationsModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // Always generate a fresh preview when opening the modal
  const { handleGeneratePreview, actionsEnabled } = useInvoice();
  React.useEffect(() => {
    if (showPreviewModal) {
      handleGeneratePreview();
    }
    // eslint-disable-next-line
  }, [showPreviewModal]);

  const {
    entries,
    previewHtml,
    pdfLoading,
    invoiceSaved,
    currentInvoiceNumber,
    handleSaveInvoice,
    profileData,
    total,
    summary,
    selectedPractice,
    selectedPractices,
    rateType,
    rateValue
  } = useInvoice();

  React.useEffect(() => {
    let isMounted = true;
    async function resolvePractice() {
      let practice = selectedPractice;
      if (typeof selectedPractice === 'string') {
        try {
          const practiceFromUtil = await practiceDataUtil.getPracticeByCode(selectedPractice);
          if (practiceFromUtil) practice = practiceFromUtil;
          else {
            const practiceFromService = await practiceService.getPracticeById(selectedPractice, selectedPractices, profileData?.uid);
            if (practiceFromService) practice = practiceFromService;
          }
        } catch {}
      }
      if (isMounted) setResolvedPractice(practice);
    }
    resolvePractice();
    return () => { isMounted = false; };
  }, [selectedPractice, selectedPractices, profileData?.uid]);

  // Save invoice handler
  const handleSave = async () => {
    setLoading(l => ({ ...l, save: true }));
    setStatus({ type: null, message: "" });
    try {
      const result = await handleSaveInvoice();
      setStatus({ type: result ? 'success' : 'info', message: result ? "Invoice saved successfully" : "No changes detected" });
      setTimeout(() => setStatus({ type: null, message: "" }), 2500);
    } catch {
      setStatus({ type: 'error', message: "Failed to save invoice" });
      setTimeout(() => setStatus({ type: null, message: "" }), 4000);
    } finally {
      setLoading(l => ({ ...l, save: false }));
    }
  };

  // Open PDF in new tab (default, mobile-friendly)
  const handleOpenPdfTab = async () => {
    setLoading(l => ({ ...l, pdf: true }));
    setStatus({ type: null, message: "" });
    let practiceToUse = selectedPractice;
    if (typeof selectedPractice === 'string') {
      try {
        const practiceFromUtil = await practiceDataUtil.getPracticeByCode(selectedPractice);
        if (practiceFromUtil) practiceToUse = practiceFromUtil;
        else {
          const practiceFromService = await practiceService.getPracticeById(selectedPractice, selectedPractices, profileData?.uid);
          if (practiceFromService) practiceToUse = practiceFromService;
        }
      } catch {}
    }
    try {
      const pdfBlob = await generateInvoicePDFBlob({
        practice: practiceToUse,
        entries,
        total,
        invoiceNumber: currentInvoiceNumber,
        profile: profileData,
        rateType,
        rateValue
      });
      if (pdfBlob instanceof Blob) {
        const blobUrl = URL.createObjectURL(pdfBlob);
        setPdfBlobUrl(blobUrl);
        window.open(blobUrl, '_blank');
        setModal({ open: true, type: 'pdfTab' });
      } else {
        setStatus({ type: 'error', message: 'PDF could not be opened in a new tab.' });
      }
    } catch {
      setStatus({ type: 'error', message: 'Failed to open PDF in new tab.' });
    } finally {
      setLoading(l => ({ ...l, pdf: false }));
    }
  };

  // Download PDF (secondary, fallback)
  const handlePdfDownload = async () => {
    setLoading(l => ({ ...l, pdf: true }));
    setStatus({ type: null, message: "" });
    let practiceToUse = selectedPractice;
    if (typeof selectedPractice === 'string') {
      try {
        const practiceFromUtil = await practiceDataUtil.getPracticeByCode(selectedPractice);
        if (practiceFromUtil) practiceToUse = practiceFromUtil;
        else {
          const practiceFromService = await practiceService.getPracticeById(selectedPractice, selectedPractices, profileData?.uid);
          if (practiceFromService) practiceToUse = practiceFromService;
        }
      } catch {}
    }
    try {
      const result = await generateInvoicePDF({
        practice: practiceToUse,
        entries,
        total,
        invoiceNumber: currentInvoiceNumber,
        profile: profileData,
        rateType,
        rateValue
      });
      if (result && result.doc && result.filename) {
        result.doc.save(result.filename);
        setStatus({ type: 'success', message: 'PDF downloaded successfully.' });
        setTimeout(() => setStatus({ type: null, message: "" }), 2000);
      } else {
        setStatus({ type: 'error', message: 'Failed to generate PDF.' });
      }
    } catch {
      setStatus({ type: 'error', message: 'Failed to generate PDF.' });
    } finally {
      setLoading(l => ({ ...l, pdf: false }));
    }
  };

  // Email to Me handler (one-click, modal feedback)
  const handleEmailToMe = async () => {
    setLoading(l => ({ ...l, email: true }));
    setStatus({ type: null, message: "" });
    let practiceToUse = selectedPractice;
    if (typeof selectedPractice === 'string') {
      try {
        const practiceFromUtil = await practiceDataUtil.getPracticeByCode(selectedPractice);
        if (practiceFromUtil) practiceToUse = practiceFromUtil;
        else {
          const practiceFromService = await practiceService.getPracticeById(selectedPractice, selectedPractices, profileData?.uid);
          if (practiceFromService) practiceToUse = practiceFromService;
        }
      } catch {}
    }
    try {
      const pdfBlob = await generateInvoicePDFBlob({
        practice: practiceToUse,
        entries,
        total,
        invoiceNumber: currentInvoiceNumber,
        profile: profileData,
        rateType,
        rateValue
      });
      const blobToBase64 = (blob) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      const pdfBase64 = await blobToBase64(pdfBlob);
      const userEmail = profileData?.email;
      const filename = `${practiceToUse?.prac_name?.replace(/\s+/g, '_').toLowerCase() || 'invoice'}_invoice_${currentInvoiceNumber || new Date().toISOString().split('T')[0]}.pdf`;
      const subject = `Invoice ${currentInvoiceNumber} from ${profileData?.name || 'Dental Hygienist'}`;
      const fullName = profileData?.name || 'Dental Hygienist';
      const html = `Please find your invoice attached.<br><br>Kind regards,<br>${fullName}`;
      const plainText = `Please find your invoice attached.\n\nKind regards,\n${fullName}`;
      const functions = getFunctions();
      if (window.location.hostname === 'localhost') {
        connectFunctionsEmulator(functions, 'localhost', 5001);
      }
      const sendEmailWithAttachment = httpsCallable(functions, 'sendEmailWithAttachment');
      const result = await sendEmailWithAttachment({
        to: userEmail,
        subject,
        html,
        plainText,
        filename,
        pdfBase64,
        name: fullName
      });
      if (result.data && result.data.success) {
        setModal({ open: true, type: 'emailSuccess' });
      } else {
        setModal({ open: true, type: 'emailError' });
      }
    } catch {
      setModal({ open: true, type: 'emailError' });
    } finally {
      setLoading(l => ({ ...l, email: false }));
    }
  };

  // Mobile guidance modal (always available)
  const openMobileHelp = () => setModal({ open: true, type: 'mobileHelp' });

  // Clean up blob URL on unmount
  React.useEffect(() => {
    return () => {
      if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
    };
  }, [pdfBlobUrl]);

  // Card-based, modern, mobile-friendly UI
  return (
    <Card className={cn(
      "shadow-lg w-full",
      theme === 'light' ? 'bg-white border-gray-200' : 'bg-[#2A2A3E] border-purple-500/20'
    )}>
      <CardContent className="p-3">
        <div className="flex flex-col gap-2 sm:gap-4">
          <div className="flex flex-row items-center justify-between gap-2">
            <h2 className={cn(
              "text-lg font-semibold tracking-tight",
              theme === 'light' ? 'text-black' : 'text-white'
            )}>Invoice Actions</h2>
            {/* Modern: right-aligned secondary actions, on same line as heading */}
            <div className="flex items-center gap-2">
              <button
                className={cn(
                  "flex items-center gap-1 text-xs text-gray-500 hover:text-purple-700 transition-colors px-2 py-1",
                  theme === 'light' ? '' : 'hover:text-purple-300',
                  !actionsEnabled && 'opacity-50 cursor-not-allowed'
                )}
                onClick={() => actionsEnabled && setShowCalculationsModal(true)}
                type="button"
                aria-label="Show Calculations"
                disabled={!actionsEnabled}
              >
                <Calculator className="h-4 w-4" />
                <span className="hidden sm:inline">Calculations</span>
              </button>
              <button
                className={cn(
                  "flex items-center gap-1 text-xs text-gray-500 hover:text-purple-700 transition-colors px-2 py-1",
                  theme === 'light' ? '' : 'hover:text-purple-300',
                  !actionsEnabled && 'opacity-50 cursor-not-allowed'
                )}
                onClick={() => actionsEnabled && setShowPreviewModal(true)}
                type="button"
                aria-label="Show Preview"
                disabled={!actionsEnabled}
              >
                <Eye className="h-4 w-4" />
                <span className="hidden sm:inline">Preview</span>
              </button>
            </div>
          </div>
        {/* Calculations Modal */}
        <Dialog open={showCalculationsModal} onOpenChange={setShowCalculationsModal}>
          <DialogContent className={cn(theme === 'light' ? 'bg-white text-gray-800' : 'bg-[#2A2A3E] text-gray-200')}>
            <DialogHeader>
              <DialogTitle className={cn(theme === 'light' ? 'text-purple-700' : 'text-purple-300')}>Invoice Calculations</DialogTitle>
            </DialogHeader>
            <div className="max-h-[60vh] overflow-auto">
              <InvoiceSummary hidePreviewButton />
            </div>
            <DialogFooter>
              <Button
                onClick={() => setShowCalculationsModal(false)}
                className={cn(
                  'bg-purple-600 hover:bg-purple-700 text-white border-none',
                  theme === 'light' ? '' : ''
                )}
              >
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Preview Modal */}
        <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
          <DialogContent className={cn(theme === 'light' ? 'bg-white text-gray-800' : 'bg-[#2A2A3E] text-gray-200')} style={{maxWidth:'900px', width:'95vw'}}>
            <DialogHeader>
              <DialogTitle className={cn(theme === 'light' ? 'text-purple-700' : 'text-purple-300')}>Invoice Preview</DialogTitle>
            </DialogHeader>
            <div className="max-h-[70vh] overflow-auto">
              <InvoicePreview />
            </div>
            <DialogFooter>
              <Button
                onClick={() => setShowPreviewModal(false)}
                className={cn(
                  'bg-purple-600 hover:bg-purple-700 text-white border-none',
                  theme === 'light' ? '' : ''
                )}
              >
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

          {/* Inline status feedback */}
          {status.type && (
            <div className={cn(
              "rounded-md px-3 py-2 text-sm flex items-center gap-2 border animate-fade-in",
              status.type === 'success' ? (theme === 'light' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-green-500/20 text-green-300 border-green-500/30') :
              status.type === 'error' ? (theme === 'light' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-red-500/20 text-red-300 border-red-500/30') :
              (theme === 'light' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-blue-500/20 text-blue-300 border-blue-500/30')
            )}>
              {status.type === 'success' && <Save className="h-4 w-4 text-green-500" />}
              {status.type === 'error' && <FileDown className="h-4 w-4 text-red-500" />}
              {status.type === 'info' && <Loader2 className="h-4 w-4 animate-spin text-blue-500" />}
              <span>{status.message}</span>
            </div>
          )}

          {/* Card: Main actions - Save and Download PDF on one line, Send Email to Myself below */}
          <div className="flex flex-row gap-2 sm:gap-4 mb-2">
            <Button
              variant="outline"
              onClick={handleSave}
              disabled={loading.save || !actionsEnabled}
              className={cn(
                "flex-1 flex items-center justify-center min-w-[120px]",
                loading.save ? (theme === 'light' ? 'bg-blue-100 text-blue-700 border-blue-300' : 'bg-blue-900/30 text-blue-300 border-blue-500/50') :
                invoiceSaved ? (theme === 'light' ? 'bg-green-100 text-green-700 border-green-300' : 'bg-green-600 text-white border-green-700') :
                (theme === 'light' ? 'bg-blue-100 text-blue-700 border-blue-300 hover:bg-blue-200' : 'bg-blue-600 text-white border-blue-700 hover:bg-blue-700')
              )}
            >
              {loading.save ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              {loading.save ? "Saving..." : invoiceSaved ? "Invoice Saved" : "Save Invoice"}
            </Button>
            <Button
              variant="outline"
              onClick={async () => {
                await handlePdfDownload();
                setModal({ open: true, type: 'downloaded' });
              }}
              disabled={loading.pdf || !invoiceSaved}
              className={cn(
                "flex-1 flex items-center justify-center min-w-[120px]",
                loading.pdf ? (theme === 'light' ? 'bg-purple-100 text-purple-700 border-purple-300' : 'bg-purple-900/30 text-purple-300 border-purple-500/50') :
                (theme === 'light' ? 'bg-purple-100 text-purple-700 border-purple-300 hover:bg-purple-200' : 'bg-purple-600 text-white border-purple-700 hover:bg-purple-700')
              )}
            >
              {loading.pdf ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileDown className="mr-2 h-4 w-4" />}
              {loading.pdf ? "Downloading..." : "Download PDF"}
            </Button>
          </div>
          <div className="flex flex-row gap-2 sm:gap-4 mb-4">
            <Button
              variant="outline"
              onClick={handleEmailToMe}
              disabled={loading.email || !invoiceSaved}
              className={cn(
                "flex-1 flex items-center justify-center min-w-[120px]",
                loading.email ? (theme === 'light' ? 'bg-indigo-100 text-indigo-700 border-indigo-300' : 'bg-indigo-900/30 text-indigo-300 border-indigo-500/50') :
                (theme === 'light' ? 'bg-indigo-100 text-indigo-700 border-indigo-300 hover:bg-indigo-200' : 'bg-indigo-600 text-white border-indigo-700 hover:bg-indigo-700')
              )}
            >
              {loading.email ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
              {loading.email ? "Emailing..." : "Email PDF Invoice to Me"}
            </Button>
            {/* Email sent acknowledgement */}
            {status.type === 'emailSent' && profileData?.email && (
              <span className={cn(
                'ml-2 text-xs flex items-center',
                theme === 'light' ? 'text-green-700' : 'text-green-300')}
              >
                <Save className="h-3 w-3 mr-1 inline-block" />
                Email sent to {profileData.email}
              </span>
            )}
          </div>

          {/* Download fallback (visible after PDF tab opened) */}
          {pdfBlobUrl && (
            <div className="flex flex-col sm:flex-row items-center gap-2 mt-2">
              {(() => {
                const practiceName = resolvedPractice?.prac_name?.replace(/\s+/g, '_').toLowerCase() || 'invoice';
                const filename = `${practiceName}_invoice_${currentInvoiceNumber || new Date().toISOString().split('T')[0]}.pdf`;
                return (
                  <>
                    <a
                      href={pdfBlobUrl}
                      download={filename}
                      className={cn(
                        "inline-flex items-center px-3 py-2 rounded border text-xs font-medium transition-colors",
                        theme === 'light' ? 'bg-white border-purple-200 text-purple-700 hover:bg-purple-50' : 'bg-[#2A2A3E] border-purple-500/40 text-purple-200 hover:bg-purple-700/20'
                      )}
                    >
                      <FileDown className="mr-2 h-4 w-4" /> Download PDF
                    </a>
                    <span className="text-xs text-gray-400">(If you want to save the file directly)</span>
                  </>
                );
              })()}
            </div>
          )}

          {/* Invoice number and process steps */}
          <div className="mt-4 flex flex-col gap-2">
            {currentInvoiceNumber && (
              <div className={cn(
                "text-sm",
                theme === 'light' ? 'text-gray-600' : 'text-gray-400'
              )}>
                Invoice Number: <span className={cn(
                  "font-medium",
                  theme === 'light' ? 'text-purple-700' : 'text-purple-300'
                )}>{currentInvoiceNumber}</span>
              </div>
            )}
          </div>
        </div>

        {/* MODALS */}
        <Dialog open={modal.open} onOpenChange={open => setModal(m => ({ ...m, open }))}>
          {modal.type === 'downloaded' && (
            <DialogContent className={cn(
              theme === 'light' ? 'bg-white text-gray-800 shadow-lg rounded-lg' : 'bg-[#2A2A3E] text-gray-200 shadow-md rounded-md'
            )}>
              <DialogHeader>
                <DialogTitle className={cn(theme === 'light' ? 'text-purple-800' : 'text-purple-400')}>PDF Downloaded Successfully</DialogTitle>
              </DialogHeader>
              <div className="py-4 text-sm leading-relaxed">
                {(() => {
                  const practiceName = resolvedPractice?.prac_name?.replace(/\s+/g, '_').toLowerCase() || 'invoice';
                  const filename = `${practiceName}_invoice_${currentInvoiceNumber || new Date().toISOString().split('T')[0]}.pdf`;
                  return (
                    <div className={cn('mb-3 px-3 py-2 rounded border flex flex-col items-start gap-1', 'bg-green-50 border-green-200 text-green-700 dark:bg-green-900/20 dark:border-green-500/30 dark:text-green-300')}> 
                      <div className="flex items-center gap-2">
                        <Save className="h-4 w-4" />
                        <span>PDF downloaded to your Downloads folder.</span>
                      </div>
                    </div>
                  );
                })()}
                <span className="mr-2">Can't find your downloaded file?</span>
                <button
                  onClick={() => setShowSteps(!showSteps)}
                  className="ml-2 mt-2 text-sm text-blue-600 hover:underline focus:outline-none"
                >
                  {showSteps ? 'Hide steps' : 'Show steps'}
                </button>
                {showSteps && (
                  <ol className="list-decimal pl-5 mt-2 space-y-1 break-words max-w-full">
                    {(() => {
                      const os = window.navigator.userAgent;
                      if (/Android/i.test(os)) {
                        return [
                          <li key="android1">Pull down from the top of your screen to see notifications</li>,
                          <li key="android2">Tap the download notification, or open your "Files" or "Downloads" app</li>,
                          <li key="android3">Look in the "Downloads" folder</li>,
                          <li key="android4">Tap and hold the file, then select "Share" to email it</li>
                        ];
                      } else if (/iPhone|iPad|iPod/i.test(os)) {
                        return [
                          <li key="ios1">Open the "Files" app on your device</li>,
                          <li key="ios2">Tap "Downloads" folder</li>,
                          <li key="ios3">Find your invoice file</li>,
                          <li key="ios4">Tap the share button (square with arrow up)</li>,
                          <li key="ios5">Choose "Mail" or your preferred email app</li>
                        ];
                      } else {
                        return [
                          <li key="desktop1">Look for the download bar at the bottom of your browser or check your Downloads folder</li>
                        ];
                      }
                    })()}
                  </ol>
                )}
                {(() => {
                  const practiceName = resolvedPractice?.prac_name?.replace(/\s+/g, '_').toLowerCase() || 'invoice';
                  const filename = `${practiceName}_invoice_${currentInvoiceNumber || new Date().toISOString().split('T')[0]}.pdf`;
                  return (
                    <div className={cn('mt-4 p-3 rounded bg-purple-50 border border-purple-200 text-xs text-purple-800 dark:bg-[#2A2A3E] dark:border-purple-700/40 dark:text-purple-200', 'max-w-full break-words')}>
                      <span className="font-semibold">Next step:</span> Email the downloaded file by attaching it to a new email or using your device's share function.
                    </div>
                  );
                })()}
              </div>
              <DialogFooter className="pt-4 border-t mt-4">
                <Button onClick={() => setModal({ open: false, type: null })} variant="outline" className={cn(
                  theme === 'light' ? 'text-white bg-purple-600 hover:bg-purple-700 border-purple-600' : 'text-white bg-purple-500 hover:bg-purple-600 border-purple-500'
                )}>Got it!</Button>
                <style>{`
                  .responsive-filename {
                    font-size: 0.7rem;
                    word-break: break-all;
                    max-width: 90vw;
                    display: inline-block;
                    vertical-align: middle;
                  }
                  @media (max-width: 640px) {
                    .responsive-filename {
                      font-size: 0.62rem;
                      max-width: 70vw;
                    }
                  }
                  @media (max-width: 400px) {
                    .responsive-filename {
                      font-size: 0.56rem;
                      max-width: 55vw;
                    }
                  }
                `}</style>
              </DialogFooter>
            </DialogContent>
          )}
          {modal.type === 'emailSuccess' && (
            <DialogContent className={cn(
              theme === 'light' ? 'bg-white text-gray-800 shadow-lg rounded-lg' : 'bg-[#2A2A3E] text-gray-200 shadow-md rounded-md'
            )}>
              <DialogHeader>
                <DialogTitle className={cn(theme === 'light' ? 'text-green-700' : 'text-green-300')}>Invoice Sent!</DialogTitle>
              </DialogHeader>
              <div className="py-4 text-sm leading-relaxed">
                Your invoice PDF has been emailed to you.<br />
                <ul className="list-disc pl-5 mt-2 space-y-1">
                  <li>Check your inbox (and spam folder).</li>
                  <li>You can forward the email or download the PDF from your email app.</li>
                </ul>
              </div>
              <DialogFooter className="pt-4 border-t mt-4">
                <Button onClick={() => setModal({ open: false, type: null })} variant="outline" className={cn(
                  theme === 'light' ? 'text-white bg-green-600 hover:bg-green-700 border-green-600' : 'text-white bg-green-500 hover:bg-green-600 border-green-500'
                )}>OK</Button>
              </DialogFooter>
            </DialogContent>
          )}
          {modal.type === 'emailError' && (
            <DialogContent className={cn(
              theme === 'light' ? 'bg-white text-gray-800 shadow-lg rounded-lg' : 'bg-[#2A2A3E] text-gray-200 shadow-md rounded-md'
            )}>
              <DialogHeader>
                <DialogTitle className={cn(theme === 'light' ? 'text-red-700' : 'text-red-300')}>Email Failed</DialogTitle>
              </DialogHeader>
              <div className="py-4 text-sm leading-relaxed">
                Sorry, we couldn't send your invoice email.<br />
                <ul className="list-disc pl-5 mt-2 space-y-1">
                  <li>Check your internet connection and try again.</li>
                  <li>If the problem persists, contact support.</li>
                </ul>
              </div>
              <DialogFooter className="pt-4 border-t mt-4">
                <Button onClick={() => setModal({ open: false, type: null })} variant="outline" className={cn(
                  theme === 'light' ? 'text-white bg-red-600 hover:bg-red-700 border-red-600' : 'text-white bg-red-500 hover:bg-red-600 border-red-500'
                )}>OK</Button>
              </DialogFooter>
            </DialogContent>
          )}
        </Dialog>
      {/* Responsive filename style to prevent wrapping button labels */}
      <style>{`
        .responsive-filename {
          font-size: clamp(10px, 2.5vw, 13px);
          max-width: 90vw;
          word-break: break-all;
          white-space: pre-line;
          display: inline-block;
          vertical-align: middle;
        }
        @media (min-width: 640px) {
          .responsive-filename {
            max-width: 340px;
          }
        }
      `}</style>
      </CardContent>
    </Card>
  );
});